import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { CheckCircleIcon } from '@/components/icons/check-circle';
import {
  HiringGoalDetails,
  OpeningDetails,
} from '@/components/pages/recommendation-posting-dialog/types';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { useRecommendationContext } from '@/contexts/recommendation-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { queryKeys } from '@/query-keys';
import { cn } from '@/utils/classnames';
import { durationInDays } from '@/utils/date-util';

interface BudgetRecommendationProps {
  opening: OpeningDetails;
  hiringGoal: HiringGoalDetails;
}

export function BudgetRecommendation({
  opening,
  hiringGoal,
}: BudgetRecommendationProps) {
  const { formatCurrency } = useCurrencySymbol();
  const { openRecommendationPostingDialog } = useRecommendationContext();

  const { data: budgetRecommendation } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.budgetRecommendation,
      opening.slug,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsBudgetRecommendation(
        opening.slug,
      ),
  });

  /* Check if there is a general recommendation, if so, the button should be secondary */
  const { data: generalRecommendation } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.generalRecommendation,
      opening.slug,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsGeneralRecommendations(
        opening.slug,
      ),
  });

  const hasGeneralRecommendation =
    !!generalRecommendation?.recommended_channels?.length;

  if (!budgetRecommendation) return null;

  const existingBudget = budgetRecommendation.sourcing_purchase.budget;
  const newBudget = budgetRecommendation.budget + existingBudget;
  const duration = durationInDays(
    budgetRecommendation.sourcing_purchase.starts_at,
    budgetRecommendation.sourcing_purchase.end_at,
  );

  const handleReviewAndApplyClick = () => {
    openRecommendationPostingDialog({
      type: 'budget',
      opening,
      budgetRecommendation,
      hiringGoal,
    });
  };

  return (
    <div className="border-fx-neutral-200 flex flex-col gap-3 rounded-md border p-4">
      {/* Title with Channel icon */}
      <div className="flex items-center gap-2">
        <Typography variant="body2">
          <FormattedMessage
            defaultMessage="Adjust budget for"
            description="Lead-in before the recruiting board icon in a budget recommendation card"
          />
        </Typography>

        <ChannelIcon
          variant="large"
          size="sm"
          channelSlug={budgetRecommendation.sourcing_channel.slug}
          channelName={budgetRecommendation.sourcing_channel.name}
        />

        <ChevronRight className="size-4" />
      </div>

      <div className="flex items-center gap-2">
        {/* Current budget and duration */}
        <Typography variant="small1" className="!text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Current: {budget} / {duration} days"
            description="Shows existing campaign spend and day range before suggested change"
            values={{
              budget: formatCurrency(existingBudget),
              duration,
            }}
          />
        </Typography>

        <ArrowRight className="text-fx-neutral-600 size-4" />

        {/* Recommended budget and duration */}
        <Tag
          text={
            <>
              <span className="font-bold">{formatCurrency(newBudget)}</span> /{' '}
              <FormattedMessage
                defaultMessage="{duration, plural, one {{duration} day} other {{duration} days}}"
                description="Duration label next to recommended budget in a tag; plural by day count"
                values={{ duration }}
              />
            </>
          }
          className="bg-fx-neutral-100"
        />
      </div>

      <Button
        variant={hasGeneralRecommendation ? 'secondary' : 'default'}
        className="h-8"
        onClick={handleReviewAndApplyClick}
      >
        <CheckCircleIcon
          className={cn(
            'size-4',
            !hasGeneralRecommendation && 'text-fx-primary',
          )}
        />
        <FormattedMessage
          defaultMessage="Review & apply"
          description="Primary button to open budget recommendation confirmation flow"
        />
      </Button>
    </div>
  );
}

BudgetRecommendation.Skeleton = function BudgetRecommendationSkeleton() {
  return (
    <div className="border-fx-neutral-200 flex flex-col gap-3 rounded-md border p-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-5 w-15" />
        <Skeleton className="size-4" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-33" />
        <Skeleton className="size-4" />
        <Skeleton className="h-6 w-28 !rounded-full" />
      </div>
      <Skeleton className="h-8 w-full" />
    </div>
  );
};
